import styled from '@mui/material/styles/styled';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

const hideAppBar = localStorage.getItem('hide_app_bar') === 'true';

const Root = styled(Box)(({ theme }) => ({
  height: hideAppBar ? '100%' : 'calc(100% - 64px)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.grey[50],
  overflow: 'auto',
  padding: '24px 16px',
  [theme.breakpoints.up('md')]: {
    padding: '32px 48px'
  }
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px'
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  color: theme.palette.text.primary
}));

const StatusBanner = styled(Paper)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'operational':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'partial_outage':
        return theme.palette.warning.dark;
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    padding: '24px',
    marginBottom: '24px',
    borderRadius: '12px',
    backgroundColor: alpha(getStatusColor(), 0.1),
    borderLeft: `4px solid ${getStatusColor()}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: '24px'
    }
  };
});

const StatusIndicator = styled(Box)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'operational':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'partial_outage':
        return theme.palette.warning.dark;
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: getStatusColor(),
    boxShadow: `0 0 8px ${alpha(getStatusColor(), 0.6)}`
  };
});

const StatusText = styled(Typography)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'operational':
        return theme.palette.success.dark;
      case 'degraded':
        return theme.palette.warning.dark;
      case 'partial_outage':
        return theme.palette.warning.dark;
      case 'outage':
        return theme.palette.error.dark;
      default:
        return theme.palette.grey[700];
    }
  };

  return {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: getStatusColor()
  };
});

const UptimeText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: '32px'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '16px'
}));

const ComponentCard = styled(Paper)(({ theme }) => ({
  padding: '16px 20px',
  marginBottom: '12px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[2]
  }
}));

const ComponentInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const ComponentName = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  color: theme.palette.text.primary
}));

const ComponentStatus = styled(Typography)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'operational':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'outage':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: getStatusColor(),
    textTransform: 'capitalize'
  };
});

const IncidentCard = styled(Paper)(({ theme, resolved }) => ({
  padding: '16px 20px',
  marginBottom: '12px',
  borderRadius: '8px',
  borderLeft: `3px solid ${resolved ? theme.palette.success.main : theme.palette.warning.main}`
}));

const IncidentHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px'
});

const IncidentName = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: theme.palette.text.primary
}));

const IncidentStatus = styled(Typography)(({ theme, resolved }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: resolved ? theme.palette.success.main : theme.palette.warning.main,
  textTransform: 'uppercase',
  padding: '2px 8px',
  borderRadius: '4px',
  backgroundColor: resolved 
    ? alpha(theme.palette.success.main, 0.1) 
    : alpha(theme.palette.warning.main, 0.1)
}));

const IncidentMeta = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginTop: '24px'
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '300px',
  gap: '16px'
});

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  color: theme.palette.text.secondary
}));

export {
  Root,
  Header,
  Title,
  StatusBanner,
  StatusIndicator,
  StatusText,
  UptimeText,
  Section,
  SectionTitle,
  ComponentCard,
  ComponentInfo,
  ComponentName,
  ComponentStatus,
  IncidentCard,
  IncidentHeader,
  IncidentName,
  IncidentStatus,
  IncidentMeta,
  LastUpdated,
  LoadingContainer,
  EmptyState
};

